import {
  Anchor,
  Button,
  Flex,
  LoadingOverlay,
  Modal,
  PasswordInput,
  Stack,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Link, useNavigate } from "react-router-dom";
import { login } from "../../../api/AuthAPI";
import { useState } from "react";
import { useLanguage } from "../../../stores/LanguageStore";
import { useSessionStore } from "../../../stores/UserStore";
import OAuthButtons from "./OAuthButtons";
import { modals } from "@mantine/modals";
import MobileGuard from "../MobileGuard";
import AccountLocked from "../errors/UnlockAccount/AccountLocked";
import { jwtDecode } from "jwt-decode";

const LoginForm = () => {
  const lang = useLanguage((s) => s.language);
  const url = new URLSearchParams(window.location.search);
  const navigate = useNavigate();
  const setSession = useSessionStore((store) => store.setSession);
  const [loading, setLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [mobileGuard, setMobileGuard] = useState(false);

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
      remember: true,
    },

    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
    },
  });
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    form.onSubmit((values) => {
      login({ email: values.email, password: values.password })
        .then((response) => {
          const destination = url.get("destination");

          const { access_token, refresh_token } = response.data;
          setSession({ access_token, refresh_token });
          setLoading(false);
          const tokenData = jwtDecode(response.data.access_token);
          // iat is 'Issued At' (when the server created the token, in seconds)
          const serverTimeMs = tokenData.iat * 1000;
          const clientTimeMs = Date.now();

          // Calculate the difference. If the user's clock is 4 hours behind, 
          // this will be a massive positive number that we add to Date.now() later.
          const skew = serverTimeMs - clientTimeMs;
          localStorage.setItem("clock_skew", skew.toString());

          if (window.innerWidth < 768) {
            return setMobileGuard(true);
          }
          setTimeout(() => {
            navigate(
              destination && !destination.includes("login")
                ? `${destination}`
                : "/"
            );
          }, 50);
        })
        .catch((error) => {
          let data = error.response.data;

          if (data.state_message === "incorrect_email_or_password") {
            form.setFieldError(
              "email",
              lang.errors.main.incorrect_email_or_password +
              ` ${data?.remaining_attempts
                ? `(${data?.remaining_attempts})`
                : ""
              } `
            );
          }
          if (data.state_message === "too_many_attempts") {
            form.setFieldError("email", lang.errors.main.too_many_attempts);
          }
          if (data.state_message === "incorrect_email") {
            form.setFieldError("email", lang.errors.main.incorrect_email);
          }
          if (data.state_message === "email_not_validated") {
            form.setFieldError("email", lang.errors.main.email_not_validated);
          }
          if (data.state_message === "user_account_disabled") {
            form.setFieldError("email", lang.errors.main.user_account_disabled);
          }
          if (data.state_message === "user_account_locked") {
            form.setFieldError("email", lang.errors.main.user_account_disabled);
            setIsLocked(true);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    })();
  };

  return (
    <Stack component={"form"} onSubmit={handleSubmit} w={"100%"} mt={8}>
      <Modal
        opened={mobileGuard}
        centered
        overlayProps={{ blur: 3 }}
        withCloseButton={false}
        closeOnClickOutside={false}
      >
        <MobileGuard isSignup={false} onClose={() => setMobileGuard(false)} />
      </Modal>

      <LoadingOverlay visible={loading} />
      <TextInput
        required
        label={lang.login.mail}
        placeholder={lang.signup.mail}
        size="lg"
        type="email"
        radius={"md"}
        mb={8}
        className={`animate__animated animate__fadeInUp`}
        style={{ animationDelay: ".1s" }}
        {...form.getInputProps("email")}
      />
      <PasswordInput
        required
        label={lang.login.password}
        placeholder={lang.login.password}
        size="lg"
        radius={"md"}
        className={`animate__animated animate__fadeInUp`}
        style={{ animationDelay: ".2s" }}
        {...form.getInputProps("password")}
      />

      <Flex justify={"space-between"} align={"center"}>
        {/* <Checkbox
          label={lang.login.remember_me}
          className={`animate__animated animate__fadeInUp`}
          style={{ animationDelay: ".25s" }}
          {...form.getInputProps("remember", { type: "checkbox" })}
        /> */}

        {window.innerWidth >= 768 && (
          <Anchor
            component={Link}
            to={"/password-recovery"}
            size="sm"
            ml={"auto"}
            className={`animate__animated animate__fadeInUp`}
            style={{ animationDelay: ".25s" }}
          >
            {lang.login.password_forgot}
          </Anchor>
        )}
      </Flex>

      <Stack
        justify="center"
        w={250}
        mx={"auto"}
        style={{ zIndex: 8, position: "relative" }}
      >
        <Button
          type="submit"
          mt={"md"}
          loading={loading}
          // leftSection={<IconLock size={18} />}
          size="lg"
          className={`animate__animated animate__fadeInUp`}
          style={{ animationDelay: ".3s" }}
          color="primary"
          fz={18}
        >
          {lang.login.button_label}
        </Button>

        <OAuthButtons
          className={`animate__animated animate__fadeIn`}
          style={{ animationDelay: ".4s" }}
        />
      </Stack>

      {isLocked && (
        <Modal
          opened
          closeOnClickOutside={false}
          onClose={() => setIsLocked(false)}
          centered
          size={"lg"}
        >
          <AccountLocked setIsLocked={setIsLocked} />
        </Modal>
      )}
    </Stack>
  );
};

export default LoginForm;
